// ローカルドラフト: 編集中の内容を localStorage に自動保存し、
// ブラウザを閉じても/事故ったあとも復元できるようにする。
//
// 使い方:
//   const draft = new LocalDraft({ key: "akebono:draft:page:3", debounceMs: 1000 })
//   draft.save({ html, title, tags, edit_message, base_version })  // debounce
//   draft.load()                                                    // 同期で読む
//   draft.clear()                                                   // 削除
//
// 保存対象は html / title / tags / edit_message と base_version (古さ判定用)、
// saved_at (UI 表示用 timestamp)。

const LS_NAMESPACE = "akebono:draft"
const MAX_HTML_BYTES = 1_000_000 // ~1MB 上限 (Lexical のリッチコンテンツでも十分)

export class LocalDraft {
  constructor({ key, debounceMs = 1000 } = {}) {
    this.key = key
    this.debounceMs = debounceMs
    this._timer = null
  }

  // 同期 save (現在の状態を即書き込み)
  saveNow(data) {
    if (!this._isAvailable()) return false
    const payload = {
      ...data,
      saved_at: Date.now(),
    }
    let json
    try { json = JSON.stringify(payload) }
    catch (e) { console.warn("[LocalDraft] failed to serialize", e); return false }

    if (json.length > MAX_HTML_BYTES) {
      console.warn(`[LocalDraft] payload too large (${json.length}B), skipping save`)
      return false
    }
    try {
      window.localStorage.setItem(this.key, json)
      return true
    } catch (e) {
      // QuotaExceededError 等
      console.warn("[LocalDraft] localStorage write failed", e)
      return false
    }
  }

  // debounce save
  save(data) {
    if (this._timer) clearTimeout(this._timer)
    this._timer = setTimeout(() => {
      this.saveNow(data)
      this._timer = null
    }, this.debounceMs)
  }

  flush(data) {
    if (this._timer) clearTimeout(this._timer)
    this._timer = null
    return this.saveNow(data)
  }

  load() {
    if (!this._isAvailable()) return null
    try {
      const raw = window.localStorage.getItem(this.key)
      if (!raw) return null
      return JSON.parse(raw)
    } catch (e) {
      console.warn("[LocalDraft] failed to parse", e)
      return null
    }
  }

  clear() {
    if (this._timer) { clearTimeout(this._timer); this._timer = null }
    if (!this._isAvailable()) return
    try { window.localStorage.removeItem(this.key) } catch (_) {}
  }

  _isAvailable() {
    try {
      return typeof window !== "undefined" && !!window.localStorage
    } catch (_) { return false }
  }
}

// 編集対象 (page か new) から key を生成
//   既存編集:  akebono:draft:page:<id>
//   新規作成:  akebono:draft:new:<space_slug>[:parent:<parent_id>]
export function draftKeyFor({ pageId, spaceSlug, parentId }) {
  if (pageId) return `${LS_NAMESPACE}:page:${pageId}`
  if (spaceSlug) {
    return parentId
      ? `${LS_NAMESPACE}:new:${spaceSlug}:parent:${parentId}`
      : `${LS_NAMESPACE}:new:${spaceSlug}`
  }
  return null
}

// 古い (= base_version より前の) 同一ページの draft を一掃する。
// 1 page につき draft キーは 1 つしか持たないので実は不要だが、 念のため。
export function pruneStaleDrafts({ pageId, currentBaseVersion }) {
  if (!pageId || currentBaseVersion == null) return
  const ls = window.localStorage
  for (let i = ls.length - 1; i >= 0; i--) {
    const key = ls.key(i)
    if (!key || !key.startsWith(`${LS_NAMESPACE}:page:${pageId}`)) continue
    try {
      const data = JSON.parse(ls.getItem(key))
      if (data && data.base_version != null && data.base_version < currentBaseVersion) {
        ls.removeItem(key)
      }
    } catch (_) { /* ignore */ }
  }
}

// "○○ 分前" の表示
export function relativeTime(ts) {
  if (!ts) return ""
  const sec = Math.floor((Date.now() - ts) / 1000)
  if (sec < 60)   return `${sec} 秒前`
  const min = Math.floor(sec / 60)
  if (min < 60)   return `${min} 分前`
  const hr = Math.floor(min / 60)
  if (hr < 24)    return `${hr} 時間前`
  const day = Math.floor(hr / 24)
  return `${day} 日前`
}
